import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useLocation, useMatch, useParams } from 'react-router-dom';
import { Box, Tab, Tabs } from '@hopskipdrive/react-components';

import { actions, subjects } from '../../contexts/AbilityContext';
import ProtectedRoute from '../../routes/ProtectedRoute';
import { ActionBar } from '../../shared/components';
import { useNavigation } from '../../shared/hooks/useNavigation';

const HeaderHostContext = createContext<HTMLDivElement | null | undefined>(undefined);
const RiderCountContext = createContext<(count: number | undefined) => void>(() => undefined);

type LocationTab = 'general' | 'riders';

export const LocationDetailsLayout: FC = () => {
  const [headerHost, setHeaderHost] = useState<HTMLDivElement | null>(null);
  const [riderCount, setRiderCount] = useState<number>();
  const { locationId } = useParams();
  const { state } = useLocation();
  const { navigateTo } = useNavigation();
  const isRidersRoute = Boolean(useMatch('/locations/:locationId/rider_instructions'));
  const currentTab: LocationTab = isRidersRoute ? 'riders' : 'general';
  const basePath = `/locations/${locationId}`;

  return (
    <ProtectedRoute allowedAction={actions.read} allowedSubject={isRidersRoute ? subjects.rider : subjects.location}>
      <HeaderHostContext.Provider value={headerHost}>
        <RiderCountContext.Provider value={setRiderCount}>
          <div ref={setHeaderHost} data-testid="location-details-header-host" style={{ display: 'contents' }} />
          <Box display="flex" flexDirection="column" minWidth={0} gap={6} pb={6}>
            <Tabs
              value={currentTab}
              aria-label="Location details"
              showIndicator
              variant="scrollable"
              scrollButtons="auto"
              onChange={(_, value: LocationTab) =>
                navigateTo(value === 'general' ? basePath : `${basePath}/rider_instructions`, state)
              }
              data-testid="location-details-tabs"
            >
              <Tab
                id="location-general-tab"
                aria-controls="location-general-panel"
                label="General info"
                value="general"
              />
              <Tab
                id="location-riders-tab"
                aria-controls="location-riders-panel"
                label={`Riders${isRidersRoute && riderCount !== undefined ? ` (${riderCount})` : ''}`}
                value="riders"
              />
            </Tabs>
            <div role="tabpanel" id={`location-${currentTab}-panel`} aria-labelledby={`location-${currentTab}-tab`}>
              <Outlet />
            </div>
          </Box>
        </RiderCountContext.Provider>
      </HeaderHostContext.Provider>
    </ProtectedRoute>
  );
};

export const LocationDetailsHeader: FC<{ children: ReactNode; riderCount?: number }> = ({ children, riderCount }) => {
  const headerHost = useContext(HeaderHostContext);
  const setRiderCount = useContext(RiderCountContext);

  useEffect(() => {
    setRiderCount(riderCount);

    return () => setRiderCount(undefined);
  }, [riderCount, setRiderCount]);

  if (headerHost === undefined) {
    throw new Error('LocationDetailsHeader must be rendered within LocationDetailsLayout');
  }

  return headerHost ? createPortal(<ActionBar>{children}</ActionBar>, headerHost) : null;
};
